import { useState, useRef, useEffect, useCallback, useMemo, useId } from "react";
import {
  Search,
  ArrowRight,
  ArrowDownUp,
  X,
  Train,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Moon,
  Filter,
  Clock,
  MapPin as MapPinIcon,
  Plus,
  Check,

  Ticket,
  Sparkles,
  poiIconAndColor,
} from "../data/design-tokens";
import type { City } from "../data/types";
export type { SearchFilters } from "../data/types";
import { searchStationsLive, groupStations } from "../services/hafasService";
import { getTrainlineUrl, getOmioUrl } from "./AffiliateLinks";

import { useSearch } from "../contexts/SearchContext";
import { useTrip } from "../contexts/TripContext";

// ─── Station Input ───────────────────────────────────────────────────────

function StationInput({
  label,
  value,
  onChange,
  placeholder,
  tripStopIds,
}: Readonly<{
  label: string;
  value: City | null;
  onChange: (station: City | null) => void;
  placeholder: string;
  tripStopIds?: Set<string>;
}>) {
  const [query, setQuery] = useState("");
  const inputId = useId();
  const [results, setResults] = useState<City[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Collapse to city-level groups, cap at 6 to keep the list manageable
  const groups = useMemo(() => groupStations(results).slice(0, 6), [results]);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear local query when parent resets value to null (e.g. "continue from" resets destination)
  useEffect(() => {
    if (!value) {
      setQuery("");
      setResults([]);
      setOpen(false);
    }
  }, [value]);

  const handleInputChange = useCallback((val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) { abortRef.current(); abortRef.current = null; }
    if (val.length >= 2) {
      setSearching(true);
      setOpen(true);
      debounceRef.current = setTimeout(() => {
        let isInitialLoadComplete = false;
        abortRef.current = searchStationsLive(val, (stations) => {
          setResults(stations.slice(0, 8));
          if (isInitialLoadComplete) {
            // Second call — API results merged, stop spinner
            setSearching(false);
          }
          isInitialLoadComplete = true;
        });
        // Safety: stop spinner after API timeout even if API never responds
        setTimeout(() => setSearching(false), 3500);
      }, 200);
    } else {
      setResults([]);
      setOpen(false);
      setSearching(false);
    }
  }, []);

  const handleSelect = useCallback(
    (station: City) => {
      onChange(station);
      setQuery(station.city);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery("");
    setResults([]);
    inputRef.current?.focus();
  }, [onChange]);

  return (
    <div ref={wrapperRef} className="relative">
      <label htmlFor={inputId} className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 block">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={value ? value.city : query}
          onChange={(e) => {
            if (value) {
              onChange(null);
              setQuery(e.target.value);
              handleInputChange(e.target.value);
            } else {
              handleInputChange(e.target.value);
            }
          }}
          onFocus={() => {
            if (value) {
              onChange(null);
              setQuery("");
            }
            if (query.length > 0) setOpen(true);
          }}
          placeholder={placeholder}
          className="w-full pl-3 pr-8 py-2 bg-gray-50 rounded-lg border border-gray-200 focus:border-indigo-400 focus:bg-white focus:outline-none transition-colors text-[14px]"
        />
        {(value || query) && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()} station`}
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto z-50">
          {searching && results.length === 0 && (
            <div className="px-3 py-3 text-center text-[12px] text-gray-400 flex items-center justify-center gap-2">
              <Loader2 className="size-3.5 animate-spin" />
              Searching stations...
            </div>
          )}
          {!searching && results.length === 0 && query.length >= 2 && (
            <div className="px-3 py-3 text-center text-[12px] text-gray-400">
              No stations found
            </div>
          )}
          {groups.map((group) => {
            const inTrip = tripStopIds?.has(group.representative.id);
            return (
              <button
                type="button"
                key={group.key}
                onClick={() => handleSelect(group.representative)}
                className={`w-full px-3 py-2 hover:bg-indigo-50 flex items-center gap-2 text-left transition-colors ${inTrip ? "opacity-50" : ""}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 text-[13px] font-medium truncate">
                    {group.matchedAlias && group.matchedAlias.toLowerCase() !== group.cityLabel.toLowerCase()
                      ? group.matchedAlias
                      : group.cityLabel}
                  </div>
                  <div className="text-[11px] text-gray-400 truncate">
                    {group.matchedAlias && group.matchedAlias.toLowerCase() !== group.cityLabel.toLowerCase()
                      ? `${group.cityLabel}, ${group.country}`
                      : group.country}
                  </div>
                </div>
                {inTrip && (
                  <span className="text-[10px] text-indigo-400 flex-shrink-0 font-medium">In trip</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SearchPanel({ onClose }: Readonly<{ onClose?: () => void }>) {
  const {
    fromStation, toStation,
    setFromStation: onFromChange, setToStation: onToChange,
    handleSearch: onSearch,
    routes, activeRouteIndex,
    handleSelectRoute: onSelectRoute,
    loading, searchPerformed, searchError,
    filters, setFilters: onFiltersChange,
    departureTime, setDepartureTime: onDepartureTimeChange,
    handleAddRouteAndContinue: onAddRouteAndContinue,
    routePOIs, searchDate, setSearchDate, resultEndpoints,
  } = useSearch();
  const { travelDate: tripStartDate, setTravelDate, tripLegs, tripStops, setSelectedStop: onSelectStop,
    pendingConnection, getConnectionDeparture } = useTrip();
  const travelDate = searchDate;
  const onDateChange = (date: string) => {
    setSearchDate(date);
    if (tripStops.length === 0) setTravelDate(date);
  };
  const pendingFrom = tripStops.find(s => s.id === pendingConnection?.fromId);
  const pendingTo = tripStops.find(s => s.id === pendingConnection?.toId);
  const choosingOnward = !!pendingConnection && fromStation?.id === pendingConnection.fromId && toStation?.id === pendingConnection.toId;
  const resultIsOnward = tripLegs.some(leg => leg.fromStationId === resultEndpoints?.from.id && leg.toStationId === resultEndpoints?.to.id);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const hasRoutes = routes.length > 0;
  const routeListRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);

  // Set of routeIds already in the trip (for duplicate detection)
  const tripRouteIds = useMemo(() => {
    if (!tripLegs) return new Set<string>();
    return new Set(tripLegs.map((l) => l.routeId));
  }, [tripLegs]);

  // Set of stop IDs already in the trip (for dimming in station inputs)
  const tripStopIdSet = useMemo(() => {
    if (!tripStops) return undefined;
    return new Set(tripStops.map((s) => s.id));
  }, [tripStops]);

  // Detect whether the route list is scrollable and not at bottom
  const checkScroll = useCallback(() => {
    const el = routeListRef.current;
    if (!el) { setCanScrollDown(false); setCanScrollUp(false); return; }
    const isScrollable = el.scrollHeight > el.clientHeight;
    setCanScrollDown(isScrollable && el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    setCanScrollUp(isScrollable && el.scrollTop > 4);
  }, []);

  useEffect(() => {
    // Delay check so the DOM has rendered the route list
    requestAnimationFrame(() => checkScroll());
  }, [routes, checkScroll]);

  const handleScrollDown = () => {
    routeListRef.current?.scrollBy({ top: 200, behavior: "smooth" });
  };

  const handleScrollUp = () => {
    routeListRef.current?.scrollBy({ top: -200, behavior: "smooth" });
  };

  const handleSwap = () => {
    const previousFrom = fromStation;
    const previousTo = toStation;
    onFromChange(previousTo);
    onToChange(previousFrom);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch();
  };

  // Get today's date in YYYY-MM-DD format for min date (local timezone)
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const shiftDate = (days: number) => {
    const current = travelDate ? new Date(travelDate + "T00:00:00") : new Date();
    current.setDate(current.getDate() + days);
    const shifted = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
    // Don't allow going before today
    if (shifted < today) return;
    onDateChange(shifted);
    // Auto-search if a search was already performed
    if (searchPerformed && fromStation && toStation) {
      onSearch(undefined, undefined, { customDate: shifted });
    }
  };

  return (
    <div className="absolute top-4 left-4 z-[1000] w-[340px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] flex flex-col">
      {/* Main Panel */}
      <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/80 overflow-hidden flex flex-col max-h-full">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex items-center gap-2 flex-shrink-0">
          <div className="bg-indigo-600 rounded-lg p-1.5">
            <Train className="size-4 text-white" />
          </div>
          <div>
            <h1 className="text-[14px] text-gray-900" style={{ fontWeight: 600 }}>
              Via Fera
            </h1>
          </div>
          <button
            onClick={onClose}
            className="ml-auto min-h-11 min-w-11 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            title="Close"
          >
            <X className="size-3.5" />
          </button>
        </div>

        {pendingFrom && pendingTo && (
          <section aria-labelledby="onward-train-heading" className="mx-4 mb-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-indigo-950">
            <h2 id="onward-train-heading" className="text-sm font-semibold">Choose your onward train</h2>
            <output className="mt-1 block text-[13px] leading-relaxed">
              {pendingConnection?.reason === "overnight" ? `Overnight stay in ${pendingFrom.city}.` : `Continue from ${pendingFrom.city}.`}
              {" "}Choose a train to {pendingTo.city} to complete your itinerary.
            </output>
            {!choosingOnward && (
              <button type="button" className="mt-2 min-h-11 w-full rounded-md bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-700"
                onClick={() => onSearch(pendingFrom, pendingTo, getConnectionDeparture(pendingFrom))}>
                Find trains to {pendingTo.city}
              </button>
            )}
          </section>
        )}

        {/* Search Form */}
        <details open={!searchPerformed && !choosingOnward} className="flex-shrink-0 border-t border-gray-100">
          <summary className="cursor-pointer px-4 py-3 text-[13px] text-gray-700 hover:bg-gray-50">
            <span className="font-semibold">{fromStation?.city || "Departure"} → {toStation?.city || "Destination"}</span>
            <span className="mt-1 block text-xs">{travelDate} · Edit stations, date & filters</span>
          </summary>
        <form onSubmit={handleSubmit} className="px-4 pb-3 space-y-2 flex-shrink-0">
          <div className="flex flex-col gap-0">
            <StationInput
              label="From"
              value={fromStation}
              onChange={onFromChange}
              placeholder="Departure city..."
              tripStopIds={tripStopIdSet}
            />
            {/* Swap button — centered between the two inputs */}
            <div className="flex justify-center -my-3 relative z-10">
              <button
                type="button"
                onClick={handleSwap}
                aria-label="Swap departure and destination"
                className="bg-white border border-gray-200 rounded-full p-1.5 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ArrowDownUp className="size-3 text-gray-400" />
              </button>
            </div>
            <StationInput
              label="To"
              value={toStation}
              onChange={onToChange}
              placeholder="Destination city..."
              tripStopIds={tripStopIdSet}
            />
          </div>

          {/* Date Picker */}
          <div>
            <label htmlFor="travel-date" className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 block">
              Train departure date
            </label>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => shiftDate(-1)}
                disabled={travelDate <= today}
                className="flex items-center justify-center w-9 h-9 bg-gray-50 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-400 transition-colors"
                title="Day before"
              >
                <ChevronLeft className="size-3.5" />
              </button>
              <input
                id="travel-date"
                type="date"
                value={travelDate}
                onChange={(e) => onDateChange(e.target.value)}
                min={today}
                className="flex-1 min-w-0 h-9 px-3 bg-gray-50 border-x border-gray-200 focus:bg-white focus:outline-none transition-colors text-[13px] text-center appearance-none"
              />
              <button
                type="button"
                onClick={() => shiftDate(1)}
                className="flex items-center justify-center w-9 h-9 bg-gray-50 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                title="Day after"
              >
                <ChevronRight className="size-3.5" />
              </button>
            </div>
          </div>

          {tripStops.length > 0 && travelDate !== tripStartDate && (
            <p className="text-xs text-gray-500">Your trip starts {tripStartDate}. This search is for the onward train.</p>
          )}

          {/* Departure Time */}
          <div>
            <p id="departure-time-label" className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 block">
              Departure
            </p>
            <fieldset aria-labelledby="departure-time-label" className="min-w-0 flex rounded-lg border border-gray-200 overflow-hidden">
              {[
                { label: "Morning", value: "05:00" },
                { label: "Midday", value: "09:00" },
                { label: "Afternoon", value: "13:00" },
                { label: "Evening", value: "17:00" },
              ].map((slot, i) => {
                const isActive = departureTime === slot.value;
                return (
                  <button
                    type="button"
                    key={slot.value}
                    onClick={() => onDepartureTimeChange(slot.value)}
                    aria-pressed={isActive}
                    className={`flex-1 flex items-center justify-center min-h-11 text-[12px] font-medium transition-colors ${i > 0 ? "border-l border-gray-200" : ""
                      } ${isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "bg-gray-50 text-gray-500 hover:bg-white hover:text-gray-700"
                      }`}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </fieldset>
          </div>

          {/* Filters Toggle */}
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="w-full flex items-center justify-between py-2 mt-1 border-t border-gray-100 text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="size-3.5" />
              <span>Filters</span>
              {(filters.sleeperTrainsOnly || filters.maxDuration || filters.maxStations) && (
                <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded text-[10px]">
                  Active
                </span>
              )}
            </div>
            {filtersOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
          </button>

          {/* Filters Panel */}
          {filtersOpen && (
            <div className="space-y-3 pb-2 border-b border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.sleeperTrainsOnly}
                  onChange={(e) => onFiltersChange({ ...filters, sleeperTrainsOnly: e.target.checked })}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <Moon className="size-3.5 text-purple-500" />
                <span className="text-[12px] text-gray-700">Sleeper trains only</span>
              </label>

              <div>
                <label htmlFor="max-duration" className="text-[11px] text-gray-500 mb-1 block">Max Duration</label>
                <div className="flex items-center gap-2">
                  <Clock className="size-3.5 text-gray-400" />
                  <select
                    id="max-duration"
                    value={filters.maxDuration || ""}
                    onChange={(e) => onFiltersChange({ ...filters, maxDuration: e.target.value ? Number(e.target.value) : null })}
                    className="flex-1 h-9 px-2 bg-gray-50 rounded-lg border border-gray-200 focus:border-indigo-400 focus:outline-none text-[12px]"
                  >
                    <option value="">Any duration</option>
                    <option value="3">Up to 3 hours</option>
                    <option value="6">Up to 6 hours</option>
                    <option value="9">Up to 9 hours</option>
                    <option value="12">Up to 12 hours</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="max-stations" className="text-[11px] text-gray-500 mb-1 block">Max Stations</label>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="size-3.5 text-gray-400" />
                  <select
                    id="max-stations"
                    value={filters.maxStations || ""}
                    onChange={(e) => onFiltersChange({ ...filters, maxStations: e.target.value ? Number(e.target.value) : null })}
                    className="flex-1 h-9 px-2 bg-gray-50 rounded-lg border border-gray-200 focus:border-indigo-400 focus:outline-none text-[12px]"
                  >
                    <option value="">Any number</option>
                    <option value="2">Direct (no changes)</option>
                    <option value="3">Up to 1 change</option>
                    <option value="5">Up to 3 changes</option>
                    <option value="8">Up to 6 changes</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onFiltersChange({ sleeperTrainsOnly: false, maxDuration: null, maxStations: null })}
                className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={!fromStation || !toStation || loading}
            className="w-full flex items-center justify-center gap-2 min-h-11 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                <span>Finding routes...</span>
              </>
            ) : (
              <>
                <Search className="size-3.5" />
                <span>Find Routes</span>
              </>
            )}
          </button>
        </form>
        </details>

        {/* Error Message */}
        {searchError && !loading && (
          <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-[12px] text-amber-800 leading-relaxed">{searchError}</p>
            <button
              onClick={() => onSearch()}
              disabled={!fromStation || !toStation}
              className="mt-2 text-[11px] text-amber-700 hover:text-amber-900 underline underline-offset-2"
            >
              Retry search
            </button>
          </div>
        )}

        {/* No Results (non-error) */}
        {searchPerformed && !loading && !hasRoutes && !searchError && (
          <div className="mx-4 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-[12px] text-gray-500">No routes found for this connection. Try a different date or nearby stations.</p>
          </div>
        )}

        {/* Route Results */}
        {hasRoutes && (
          <div className="border-t border-gray-100 flex-1 min-h-0 flex flex-col">
            <div className="px-4 py-2 text-[13px] text-gray-500 flex-shrink-0">
              <span className="font-medium text-gray-700">{routes.length} train option{routes.length === 1 ? "" : "s"}</span>
              <p className="mt-1">Select an option to preview its path, then add it to your trip.</p>
            </div>

            <div className="relative flex-1 min-h-0 flex flex-col">
              <div
                ref={routeListRef}
                className="flex-1 min-h-0 overflow-y-auto"
                onScroll={checkScroll}
              >
                {routes.map((route, idx) => {
                  const isActive = idx === activeRouteIndex;
                  const isAlreadyAdded = tripRouteIds.has(route.id);
                  // Live routes store city names in the route name
                  const routeNameParts = route.name.split(" to ");
                  const fromCity = resultEndpoints?.from.city || routeNameParts[0] || "";
                  const toCity = resultEndpoints?.to.city || routeNameParts[1] || "";
                  return (
                    <div
                      key={route.id}
                      className={`w-full px-4 py-2.5 flex items-start gap-2.5 text-left transition-colors border-l-2 cursor-pointer ${isActive
                        ? "bg-indigo-50/80 border-l-indigo-600"
                        : "border-l-transparent hover:bg-gray-50"
                        }`}
                    >
                      <div
                        className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${isActive ? "bg-indigo-600" : "bg-gray-300"
                          }`}
                      />
                      <div className="flex-1 min-w-0">
                        <button type="button" onClick={() => onSelectRoute(idx)} className="block w-full text-left font-normal rounded focus-visible:outline-2 focus-visible:outline-indigo-600"
                          aria-pressed={isActive} aria-label={`Preview train ${idx + 1}: ${route.departureTime} to ${route.arrivalTime}, ${route.duration}`}>
                        <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-indigo-700">
                          <span>Option {idx + 1}</span>{isActive && <span>· Previewing on map</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-[13px] truncate ${isActive ? "text-indigo-700" : "text-gray-700"
                              }`}
                            style={{ fontWeight: isActive ? 600 : 400 }}
                          >
                            {fromCity}
                          </span>
                          <ArrowRight className="size-3 text-gray-400 flex-shrink-0" />
                          <span
                            className={`text-[13px] truncate ${isActive ? "text-indigo-700" : "text-gray-700"
                              }`}
                            style={{ fontWeight: isActive ? 600 : 400 }}
                          >
                            {toCity}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[11px] text-gray-400 truncate flex-1">
                            {route.stationData?.[0]?.name}
                          </span>
                          <ArrowRight className="size-3 opacity-0 flex-shrink-0" />
                          <span className="text-[11px] text-gray-400 truncate flex-1 text-right">
                            {route.stationData?.at(-1)?.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-base font-semibold text-gray-900">
                            {route.departureTime} – {route.arrivalTime}
                          </span>
                          <span className="text-[11px] text-gray-300">
                            &middot;
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {route.duration}
                          </span>
                          <span className="text-[11px] text-gray-300">
                            &middot;
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {route.frequency || "Direct"}
                          </span>
                          {route.isSleeper && !route.frequency && (
                            <>
                              <span className="text-[11px] text-gray-300">
                                &middot;
                              </span>
                              <span className="text-[11px] text-purple-500 flex items-center gap-0.5">
                                <Moon className="size-2.5" />
                                Sleeper
                              </span>
                            </>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {route.departureDate}{route.arrivalDate && route.arrivalDate !== route.departureDate && ` → ${route.arrivalDate}`}
                        </div>
                        <div className="flex items-center mt-1">
                          <span className="text-[10px] text-gray-400">
                            {route.operator}
                          </span>
                        </div>
                        </button>
                        {/* Action row: Add-to-trip pill + ticket links */}
                        {(isActive || isAlreadyAdded) && (
                          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                            {isAlreadyAdded && (
                              <span className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-emerald-50 text-sm font-medium text-emerald-700">
                                <Check className="size-4" /> In your trip
                              </span>
                            )}
                            {!isAlreadyAdded && isActive && (
                              <button type="button" onClick={() => onAddRouteAndContinue(route)}
                                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                                <Plus className="size-4" />
                                {resultIsOnward ? `Use this train to ${toCity}` : "Add this train to itinerary"}
                              </button>
                            )}
                            {isActive && fromCity && toCity && (
                              <>
                                <a
                                  href={getTrainlineUrl(fromCity, toCity, route.departureDate ?? travelDate)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-2 min-h-11 rounded text-xs transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Ticket className="size-2.5" />
                                  <span>Trainline</span>
                                </a>
                                <a
                                  href={getOmioUrl(fromCity, toCity, route.departureDate ?? travelDate)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 bg-sky-50 text-sky-700 hover:bg-sky-100 px-3 py-2 min-h-11 rounded text-xs transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Ticket className="size-2.5" />
                                  <span>Omio</span>
                                </a>
                              </>
                            )}
                          </div>
                        )}
                        {/* Along the Way — POIs near this route */}
                        {isActive && routePOIs.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-1 mb-1.5">
                              <Sparkles className="size-3" style={{ color: "#8b5cf6" }} />
                              <span className="text-[10px] text-gray-500" style={{ fontWeight: 600 }}>
                                Along the way
                              </span>
                            </div>
                            <div className="flex flex-col gap-1">
                              {routePOIs.slice(0, 3).map((poi) => {
                                const { Icon, color } = poiIconAndColor(poi.poiType!);
                                return (
                                  <button
                                    key={poi.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onSelectStop && poi.latitude && poi.longitude) {
                                        onSelectStop({
                                          ...poi,
                                          id: `poi-${poi.id}`,
                                          name: poi.poiName || poi.city,
                                        });
                                      }
                                    }}
                                    className="flex items-start gap-1.5 text-left rounded px-1.5 py-1 hover:bg-purple-50/60 transition-colors group"
                                  >
                                    <Icon className="mt-0.5 size-3 flex-shrink-0" style={{ color }} />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[11px] text-gray-700 group-hover:text-purple-700 truncate" style={{ fontWeight: 500 }}>
                                          {poi.poiName || poi.city}
                                        </span>
                                        <span className="text-[9px] text-gray-400 flex-shrink-0">
                                          {(poi.distanceKm ?? 0) < 1 ? "<1" : Math.round(poi.distanceKm ?? 0)} km off route
                                        </span>
                                      </div>
                                      {poi.description && (
                                        <p className="text-[10px] text-gray-400 line-clamp-1 leading-tight mt-0.5">
                                          {poi.description}
                                        </p>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {(canScrollUp || canScrollDown) && (
                <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 py-1.5 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none">
                  <button
                    onClick={handleScrollUp}
                    disabled={!canScrollUp}
                    title="Scroll up"
                    className="pointer-events-auto p-1 rounded-full bg-white/90 border border-gray-200 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:border-gray-200 transition-colors shadow-sm"
                  >
                    <ChevronUp className="size-4" />
                  </button>
                  <button
                    onClick={handleScrollDown}
                    disabled={!canScrollDown}
                    title="Scroll down"
                    className="pointer-events-auto p-1 rounded-full bg-white/90 border border-gray-200 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:border-gray-200 transition-colors shadow-sm"
                  >
                    <ChevronDown className="size-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
